import React, { createContext, useCallback, useContext, useState } from "react";
import { 
  CargoDocumentTypeApiResponse, 
  getListaCargoDocumentType, 
  getCargoDocumentTypeById, 
  createCargoDocumentType, 
  updateCargoDocumentType, 
  deleteCargoDocumentType, 
  getListaCargoDocumentTypePaginated,
  getListaCargoDocument,
} from "../../services/cargoDocumentTypeService";
import { getErrorMessage } from "../../locales/translationUtils";
import LoadingOverlay from "../../components/Loading";
import CargoDocumentType from "../../types/cargoDocumentType";
import Paginacao from "../../types/paginacao";
import { ApiResponse } from "../../services/api";

interface CargoDocumentTypeContextProps {
  apiResponse: ApiResponse<CargoDocumentType> | null;
  cargosDocumentType: CargoDocumentType[] | null;
  cargoDocumentTypeById: CargoDocumentTypeApiResponse | null;
  loading: boolean;
  error: string | null;
  fetchCargoDocumentType: () => void;
  fetchCargoDocumentTypeById: (id: number) => Promise<CargoDocumentTypeApiResponse | null>;
  fetchCargoDocumentTypePaginated: (paginacao: Paginacao | null) => void;
  postCargoDocumentType: (cargo: CargoDocumentType) => Promise<CargoDocumentTypeApiResponse | null>;
  putCargoDocumentType: (id: number, cargo: CargoDocumentType) => Promise<CargoDocumentTypeApiResponse | null>;
  excluiCargoDocumentType: (id: number) => Promise<boolean>;
}

interface CargoDocumentTypeProviderProps {
  children: React.ReactNode;
}

const CargoDocumentTypeContext = createContext<CargoDocumentTypeContextProps | null>(null);

const CargoDocumentTypeProvider: React.FC<CargoDocumentTypeProviderProps> = ({ children }) => {
  const [apiResponse, setApiResponse] = useState<ApiResponse<CargoDocumentType> | null>(null);
  const [cargosDocumentType, setCargoDocumentType] = useState<CargoDocumentType[] | null>(null);
  const [cargoDocumentTypeById, setCargoDocumentTypeById] = useState<CargoDocumentTypeApiResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCargoDocumentType = useCallback(async () => {
    setError(null);
    try {
      setLoading(true);
      const response = await getListaCargoDocumentType();
      setCargoDocumentType(response.values || []);
    } catch (err) {
      setError(getErrorMessage("fetchCargoDocumentTypeError"));
    } finally {
      setLoading(false);
    }
  }, []);

    const fetchCargoDocumentTypePaginated = useCallback(async (paginacao: Paginacao | null) => {
    setError(null);
    try {
      setLoading(true);
      const response = (paginacao) ? await getListaCargoDocumentTypePaginated(paginacao) : await getListaCargoDocument();
      setApiResponse(response);
      setCargoDocumentType(response.data.values || []);
    } catch (err) {
      setCargoDocumentType([]);
      setError(getErrorMessage("fetchCargoDocumentTypeError"));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCargoDocumentTypeById = useCallback(async (id: number) => {
    setError(null);
    try {
      setLoading(true);
      const response = await getCargoDocumentTypeById(id);
      setCargoDocumentTypeById(response);
      return response;
    } catch (err) {
      setError(getErrorMessage("fetchCargoDocumentTypeByIdError"));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const postCargoDocumentType = useCallback(async (cargo: CargoDocumentType) => {
    setError(null);
    try {
      setLoading(true);
      const response = await createCargoDocumentType(cargo);
      fetchCargoDocumentType();
      return response;
    } catch (err) {
      setError(getErrorMessage("createCargoDocumentTypeError"));
      return null;
    } finally {
      setLoading(false);
    }
  }, [fetchCargoDocumentType]);

  const putCargoDocumentType = useCallback(async (id: number, cargo: CargoDocumentType) => {
    setError(null);
    try {
      setLoading(true);
      const response = await updateCargoDocumentType(id, cargo);
      fetchCargoDocumentType();
      return response;
    } catch (err) {
      setError(getErrorMessage("updateCargoDocumentTypeError"));
      return null;
    } finally {
      setLoading(false);
    }
  }, [fetchCargoDocumentType]);

  const excluiCargoDocumentType = useCallback(async (id: number) => {
    setError(null);
    try {
      setLoading(true);
      await deleteCargoDocumentType(id);
      fetchCargoDocumentType();
      return true;
    } catch (err) {
      setError(getErrorMessage("deleteCargoDocumentTypeError"));
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchCargoDocumentType]);

  return (
    <CargoDocumentTypeContext.Provider 
      value={{ 
        apiResponse,
        cargosDocumentType, 
        cargoDocumentTypeById, 
        loading, 
        error, 
        fetchCargoDocumentType, 
        fetchCargoDocumentTypeById,
        fetchCargoDocumentTypePaginated,
        postCargoDocumentType, 
        putCargoDocumentType, 
        excluiCargoDocumentType 
      }}
    >
      <LoadingOverlay isLoading={loading} />
      {children}
    </CargoDocumentTypeContext.Provider>
  );
};

export default CargoDocumentTypeProvider;

export const useCargoDocumentType = (): CargoDocumentTypeContextProps => {
  const context = useContext(CargoDocumentTypeContext);
  if (!context) {
    throw new Error(
      getErrorMessage("wrongUseProviderError", {
        auth: useCargoDocumentType?.name,
        providerName: CargoDocumentTypeProvider?.name,
      })
    );
  }
  return context;
};
